/**
 * Entity Search Provider — allows modules to register searchable entity types
 * with the Command Palette. Context-aware: the active module's entities rank higher.
 */

export interface EntityResult {
  id: string
  label: string
  sublabel?: string
  icon?: string
  module: string
  href?: string
  onSelect?: () => void
}

export interface EntitySearchProvider {
  id: string
  module: string
  label: string
  search: (query: string, limit?: number) => Promise<EntityResult[]> | EntityResult[]
}

type ProviderMap = Map<string, EntitySearchProvider>

class EntitySearchRegistryImpl {
  private providers: ProviderMap = new Map()
  private listeners: Set<() => void> = new Set()
  private _activeModule: string | null = null

  get activeModule() {
    return this._activeModule
  }

  setActiveModule(module: string | null) {
    this._activeModule = module
    this.notify()
  }

  register(provider: EntitySearchProvider): () => void {
    this.providers.set(provider.id, provider)
    this.notify()
    return () => {
      this.providers.delete(provider.id)
      this.notify()
    }
  }

  listProviders(): EntitySearchProvider[] {
    return Array.from(this.providers.values())
  }

  async search(query: string, limit = 5): Promise<EntityResult[]> {
    if (!query.trim()) return []

    const providers = Array.from(this.providers.values())

    // Active module's provider runs first with higher limit
    const active = providers.filter(p => p.module === this._activeModule)
    const others = providers.filter(p => p.module !== this._activeModule)

    const [activeResults, otherResults] = await Promise.all([
      Promise.all(active.map(p => Promise.resolve(p.search(query, limit)))),
      Promise.all(others.map(p => Promise.resolve(p.search(query, Math.max(2, Math.floor(limit / 2))))),
      ),
    ])

    const results: EntityResult[] = [
      ...activeResults.flat(),
      ...otherResults.flat(),
    ]

    return results.slice(0, limit * 2)
  }

  on(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify() {
    for (const l of this.listeners) {
      try { l() } catch { /* ignore */ }
    }
  }
}

export const globalEntitySearch = new EntitySearchRegistryImpl()
